import { EnvironmentId } from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import type * as Stream from "effect/Stream";

import type { HostEnvironmentEntry } from "./host.ts";
import type { ConnectionAttemptError } from "./model.ts";

/**
 * How much of one Environment's GitHub access the other Environments on this device may use.
 *
 * `read` lets another machine read pull request data through this one. `read-write` also lets it
 * act — comment, review, merge — which can reach further than the machine that owns the
 * credential would itself, so the two are never collapsed into a single "on".
 */
export const GitHubRoutingPermission = Schema.Literals(["off", "read", "read-write"]);
export type GitHubRoutingPermission = typeof GitHubRoutingPermission.Type;

export const DEFAULT_GITHUB_ROUTING_PERMISSION: GitHubRoutingPermission = "off";

export const StoredGitHubRoutingPermission = Schema.Struct({
  environmentId: EnvironmentId,
  /** The destination the grant was made against. A grant whose key no longer matches is void. */
  connectionKey: Schema.String,
  permission: GitHubRoutingPermission,
});
export type StoredGitHubRoutingPermission = typeof StoredGitHubRoutingPermission.Type;

/**
 * The saved destination a GitHub routing grant belongs to.
 *
 * Trust belongs to the destination the user typed, never to an Environment ID a server advertises:
 * an ID alone is a claim made by whatever answers at the other end. Pinning the grant to the SSH
 * destination means that re-pointing a Linked Computer at a different host revokes it rather than
 * silently carrying it across, which is the whole reason this is keyed at all.
 *
 * A Linked Computer is keyed by the Environment identity itself (ADR-0122). That is a stronger
 * pin than the SSH destination this used to record, not a weaker one: the identity is the
 * Environment's own key-backed one, so pointing at a different machine is a different id and
 * voids the grant, while the endpoint hostname changing under the same Environment does not.
 */
export function gitHubRoutingConnectionKey(entry: HostEnvironmentEntry): string {
  const target = entry.target;
  switch (target._tag) {
    case "PrimaryConnectionTarget":
      // This machine. Its own identity is not in question, so the Environment ID is enough.
      return JSON.stringify([target._tag, target.environmentId]);
    case "RemoteConnectionTarget":
      return JSON.stringify([target._tag, target.environmentId]);
    case "PairedConnectionTarget":
      // Pinned to the address as well: a link names one place, and the same identity answering
      // somewhere else is a different pairing.
      return JSON.stringify([target._tag, target.environmentId, target.httpBaseUrl]);
    case "SshConnectionTarget":
      // An SSH Environment is pinned to the destination as well as the identity. The identity is
      // the Environment's own, but the alias that reaches it is the user's to edit, and an alias
      // repointed at another machine must not carry a grant across with it.
      return JSON.stringify([
        target._tag,
        target.environmentId,
        target.ssh.alias,
        target.ssh.hostname,
        target.ssh.username ?? "",
        target.ssh.port ?? "",
      ]);
  }
}

/**
 * The permission in force for an Environment. A stored grant applies only when its key still
 * matches the destination now saved for that Environment; anything else reads as `off`.
 */
export function gitHubRoutingPermissionFor(
  entry: HostEnvironmentEntry,
  permissions: ReadonlyArray<StoredGitHubRoutingPermission>,
): GitHubRoutingPermission {
  const key = gitHubRoutingConnectionKey(entry);
  const stored = permissions.find(
    (permission) =>
      permission.environmentId === entry.target.environmentId && permission.connectionKey === key,
  );
  return stored?.permission ?? DEFAULT_GITHUB_ROUTING_PERMISSION;
}

/** Whether the grant permits acting on GitHub, not merely reading it. */
export function permitsGitHubWrites(permission: GitHubRoutingPermission): boolean {
  return permission === "read-write";
}

/** Whether the grant permits reading pull request data at all. */
export function permitsGitHubReads(permission: GitHubRoutingPermission): boolean {
  return permission !== "off";
}

/**
 * Per-device storage for GitHub routing grants. Deliberately device-scoped and never carried to
 * the account: lending one machine's GitHub credential to another is a decision about the two
 * machines in front of the user, not something a sign-in elsewhere should be able to arrange.
 */
export class GitHubRoutingPermissions extends Context.Service<
  GitHubRoutingPermissions,
  {
    readonly changes: Stream.Stream<ReadonlyArray<StoredGitHubRoutingPermission>>;
    readonly current: Effect.Effect<ReadonlyArray<StoredGitHubRoutingPermission>>;
    readonly set: (input: {
      readonly environmentId: EnvironmentId;
      readonly connectionKey: string;
      readonly permission: GitHubRoutingPermission;
    }) => Effect.Effect<void, ConnectionAttemptError>;
    /** Drops every grant for an Environment. Used when its destination changes or it is removed. */
    readonly forget: (environmentId: EnvironmentId) => Effect.Effect<void, ConnectionAttemptError>;
  }
>()("@afar-research/client-runtime/connection/githubRouting/GitHubRoutingPermissions") {}

export const make = (service: GitHubRoutingPermissions["Service"]) =>
  GitHubRoutingPermissions.of(service);

/** A machine's GitHub identity, as its own source-control discovery reports it. */
export interface GitHubRoutingIdentity {
  readonly host: string;
  readonly account: string;
}

/**
 * Whether two machines are signed in to the same GitHub account on the same host.
 *
 * This is the guard that makes routing safe. A grant says the user is willing to lend one
 * machine's access to another; it does not say the two machines are the same person. Routing a
 * request to a machine signed in as somebody else would read or act as that other account, which
 * is never what granting meant.
 */
export function isSameGitHubIdentity(
  left: GitHubRoutingIdentity | null,
  right: GitHubRoutingIdentity | null,
): boolean {
  if (left === null || right === null) return false;
  return (
    left.host.toLowerCase() === right.host.toLowerCase() &&
    left.account.toLowerCase() === right.account.toLowerCase()
  );
}

/**
 * Whether work for `origin` may run on `destination`.
 *
 * Both ends must grant, because routing lends access in one direction and exposes a repository in
 * the other. Acting needs `read-write` at both ends: a machine the user only let others read
 * through must not become the one that merges.
 */
export function isGitHubRoutingAllowed(input: {
  readonly origin: GitHubRoutingPermission;
  readonly destination: GitHubRoutingPermission;
  readonly write: boolean;
}): boolean {
  return input.write
    ? input.origin === "read-write" && input.destination === "read-write"
    : permitsGitHubReads(input.origin) && permitsGitHubReads(input.destination);
}
